import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiTrash2 } from 'react-icons/fi';
import { adminAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { hasAdminManagerRole } from '../../../utils/roles';

// Zoho-style inventory adjustment: pick a customer and warehouse, add item
// lines with signed quantity changes, and apply. Negative removes stock
// (damaged/lost/miscount), positive adds it. A SKU mix-up is one document
// with a negative line for the wrong SKU and a positive line for the right
// one. The item picker shows system stock next to the actual unit count so
// aggregate drift is visible (and repairable) at the point of correction.
const REASON_OPTIONS = [
  { value: 'count_correction', label: 'Count correction (cycle count / audit)' },
  { value: 'sku_correction', label: 'SKU correction (wrong item identified)' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'lost', label: 'Lost' },
  { value: 'found', label: 'Found' },
  { value: 'vendor_misship', label: 'Vendor mis-shipment' },
  { value: 'other', label: 'Other' }
];

const CreateAdjustment = () => {
  const { user } = useAuth();
  const canCorrect = hasAdminManagerRole(user);
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [search, setSearch] = useState('');
  const [lines, setLines] = useState([]);
  const [reasonCode, setReasonCode] = useState('count_correction');
  const [customerReason, setCustomerReason] = useState('');
  const [internalNotes, setInternalNotes] = useState('');

  const { data: customersData } = useQuery(
    'customers-filter-options',
    () => adminAPI.getCustomers({ limit: 500 }),
    { staleTime: 5 * 60 * 1000 }
  );
  const customers = customersData?.customers || [];

  const { data: warehousesData } = useQuery(
    'warehouses-all',
    () => adminAPI.getWarehouses(),
    { staleTime: 5 * 60 * 1000 }
  );
  const warehouses = warehousesData?.data || warehousesData?.warehouses || (Array.isArray(warehousesData) ? warehousesData : []);

  const stockStatusQuery = useQuery(
    ['adjustment-stock-status', companyId, warehouseId, search],
    () => adminAPI.getAdjustmentStockStatus({ companyId, warehouseId, search }),
    { enabled: !!companyId && !!warehouseId, keepPreviousData: true }
  );
  const stockRows = stockStatusQuery.data?.data || [];

  const addLine = (row) => {
    if (lines.some((line) => line.inventoryItemId === row.id)) return;
    setLines([...lines, {
      inventoryItemId: row.id,
      sku: row.product_sku,
      name: row.product_name,
      currentStock: row.stock_quantity,
      assetCount: row.asset_count,
      quantityDelta: '',
      declaredUnitValue: '',
      // Stock-only lines fix the aggregate count without touching physical
      // units -- the repair tool when Stock and Units disagree (drift).
      stockOnly: false
    }]);
  };

  const updateLineDeclaredValue = (index, value) => {
    const next = [...lines];
    next[index] = { ...next[index], declaredUnitValue: value };
    setLines(next);
  };

  const updateLineDelta = (index, value) => {
    const next = [...lines];
    // Allow empty string and a bare minus sign while typing.
    next[index].quantityDelta = value === '' || value === '-' ? value : String(parseInt(value, 10) || '');
    setLines(next);
  };

  const toggleStockOnly = (index) => {
    const next = [...lines];
    next[index].stockOnly = !next[index].stockOnly;
    setLines(next);
  };

  const removeLine = (index) => {
    setLines(lines.filter((_, i) => i !== index));
  };

  const createMutation = useMutation({
    mutationFn: (payload) => adminAPI.createAdjustment(payload),
    onSuccess: (result) => {
      const review = result?.correction?.requiresBillingReview;
      toast.success(`${result?.message || 'Adjustment applied.'}${review ? ' Flagged for billing review.' : ''}`);
      navigate(`/admin/corrections/${result.correction.id}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to apply adjustment');
    }
  });

  const validLines = lines.filter((line) => {
    const delta = parseInt(line.quantityDelta, 10);
    return Number.isInteger(delta) && delta !== 0;
  });
  // Declared value (adjust-in lines): currency shape mirrors the
  // backend validator -- at most two decimals, DECIMAL(10,2) max. An
  // invalid entry blocks submission rather than being silently
  // converted to null (which would create uninsured assets).
  const declaredValueValid = (line) => {
    // Only physical positive adjust-ins carry a declared value; a stale
    // invalid entry on a line switched to adjust-out or stock-only sits
    // in a DISABLED input and must not block submission forever.
    const delta = parseInt(line.quantityDelta, 10);
    const isPhysicalAdjustIn = Number.isInteger(delta) && delta > 0 && line.stockOnly !== true;
    if (!isPhysicalAdjustIn) return true;
    const raw = line.declaredUnitValue;
    if (raw == null || raw === '') return true;
    if (!/^(\d+)?(\.\d{1,2})?$/.test(String(raw).trim())) return false;
    const value = parseFloat(raw);
    return Number.isFinite(value) && value >= 0 && value <= 99999999.99;
  };
  const allDeclaredValid = lines.every(declaredValueValid);
  const canSubmit = companyId && warehouseId && validLines.length > 0 && validLines.length === lines.length && allDeclaredValid && !createMutation.isLoading;

  const handleSubmit = () => {
    createMutation.mutate({
      companyId,
      warehouseId,
      reasonCode,
      customerReason: customerReason.trim() || null,
      internalNotes: internalNotes.trim() || null,
      lines: validLines.map((line) => ({
        inventoryItemId: line.inventoryItemId,
        quantityDelta: parseInt(line.quantityDelta, 10),
        stockOnly: line.stockOnly === true,
        // RF8: insurance bills from asset rows -- adjust-ins carry a
        // declared value per unit so corrected-in stock stays insured.
        declaredUnitValue: parseInt(line.quantityDelta, 10) > 0 && line.stockOnly !== true && parseFloat(line.declaredUnitValue) > 0
          ? parseFloat(line.declaredUnitValue)
          : null
      }))
    });
  };

  const resetContext = () => {
    setLines([]);
    setSearch('');
  };

  // Correction mutations are role-gated on the backend (RF15:
  // admin/manager) -- this form would only fail at submission.
  if (!canCorrect) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4">
        <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4 text-sm text-yellow-800">
          Inventory corrections require admin or manager privileges.
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <Link to="/admin/corrections" className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800 mb-4">
        <FiArrowLeft className="mr-1 h-4 w-4" /> Back to corrections
      </Link>

      <h1 className="text-2xl font-semibold text-gray-900 mb-1">New Inventory Adjustment</h1>
      <p className="text-sm text-gray-500 mb-6">
        Correct system stock to match physical reality. Use a negative quantity to remove
        stock and a positive quantity to add it; a SKU mix-up is one adjustment with both.
      </p>

      {/* Context */}
      <div className="bg-white shadow rounded-lg p-4 mb-6 flex flex-wrap gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Customer</label>
          <select
            value={companyId}
            onChange={(e) => { setCompanyId(e.target.value); resetContext(); }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm min-w-[220px]"
          >
            <option value="">Select customer...</option>
            {customers.map((customer) => (
              <option key={customer.id} value={customer.id}>{customer.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Warehouse</label>
          <select
            value={warehouseId}
            onChange={(e) => { setWarehouseId(e.target.value); resetContext(); }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm min-w-[180px]"
          >
            <option value="">Select warehouse...</option>
            {warehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Item picker with drift visibility */}
      {companyId && warehouseId && (
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="p-4 border-b border-gray-200">
            <label className="block text-xs font-medium text-gray-500 mb-1">Add items</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by SKU or name..."
              className="w-full sm:w-96 border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
            {stockRows.map((row) => {
              const added = lines.some((line) => line.inventoryItemId === row.id);
              return (
                <button
                  key={row.id}
                  onClick={() => addLine(row)}
                  disabled={added}
                  className={`w-full px-4 py-2 flex items-center justify-between text-left text-sm ${added ? 'bg-gray-50 text-gray-400' : 'hover:bg-indigo-50'}`}
                >
                  <span>
                    <span className="font-medium">{row.product_sku}</span>
                    {row.product_name ? <span className="text-gray-500"> — {row.product_name}</span> : null}
                  </span>
                  <span className="flex items-center gap-3 text-xs">
                    <span className="text-gray-500">Stock: <span className="font-medium text-gray-900">{row.stock_quantity}</span></span>
                    <span className="text-gray-500">Units: <span className="font-medium text-gray-900">{row.asset_count}</span></span>
                    {row.drift !== 0 && (
                      <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 font-medium" title="System stock differs from tracked unit count">
                        drift {row.drift > 0 ? '+' : ''}{row.drift}
                      </span>
                    )}
                    {added && <span className="text-gray-400">added</span>}
                  </span>
                </button>
              );
            })}
            {!stockStatusQuery.isLoading && stockRows.length === 0 && (
              <p className="px-4 py-4 text-sm text-gray-500">No items found.</p>
            )}
          </div>
        </div>
      )}

      {/* Lines */}
      {lines.length > 0 && (
        <div className="bg-white shadow rounded-lg mb-6 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Current Stock</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Adjustment</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">New Qty</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" title="Declared value per unit for insurance (adjust-in lines only)">Declared $/Unit</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" title="Fix the stock count without touching physical units — use when Stock and Units disagree">
                  Stock Only
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {lines.map((line, index) => {
                const delta = parseInt(line.quantityDelta, 10);
                const hasDelta = Number.isInteger(delta) && delta !== 0;
                const newQty = hasDelta ? Math.max(0, line.currentStock + delta) : null;
                return (
                  <tr key={line.inventoryItemId}>
                    <td className="px-4 py-3 text-sm">
                      <span className="font-medium text-gray-900">{line.sku}</span>
                      {line.name ? <span className="text-gray-500"> — {line.name}</span> : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-600">{line.currentStock}</td>
                    <td className="px-4 py-3 text-right">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={line.quantityDelta}
                        onChange={(e) => updateLineDelta(index, e.target.value)}
                        placeholder="+/-"
                        className={`w-24 border rounded-md px-2 py-1 text-sm text-right ${hasDelta ? (delta < 0 ? 'border-red-300 text-red-700' : 'border-green-300 text-green-700') : 'border-gray-300'}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                      {newQty !== null ? newQty : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={line.declaredUnitValue}
                        onChange={(e) => updateLineDeclaredValue(index, e.target.value)}
                        disabled={!hasDelta || delta < 0 || line.stockOnly}
                        placeholder="$/unit"
                        title="Declared value per unit for insurance (adjust-in lines only) -- max two decimals, up to $99,999,999.99"
                        className={`w-24 border rounded-md px-2 py-1 text-sm text-right disabled:bg-gray-50 disabled:text-gray-400 ${declaredValueValid(line) ? 'border-gray-300' : 'border-red-400 text-red-700'}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={line.stockOnly}
                        onChange={() => toggleStockOnly(index)}
                        className="h-4 w-4 rounded border-gray-300"
                        aria-label="Adjust the stock count only, without changing physical units"
                        title="Fix the stock count without touching physical units — use when Stock and Units disagree"
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => removeLine(index)} className="text-gray-400 hover:text-red-600" aria-label="Remove line">
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Reasons */}
      {lines.length > 0 && (
        <div className="bg-white shadow rounded-lg p-4 mb-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <select
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm min-w-[280px]"
            >
              {REASON_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer-facing reason <span className="text-gray-400 font-normal">(optional, visible to the customer)</span>
            </label>
            <textarea
              value={customerReason}
              onChange={(e) => setCustomerReason(e.target.value)}
              rows={2}
              maxLength={2000}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Internal notes <span className="text-gray-400 font-normal">(staff only)</span>
            </label>
            <textarea
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              rows={2}
              maxLength={5000}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {createMutation.isLoading ? 'Applying...' : 'Apply Adjustment'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateAdjustment;
